import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AlertCircle, X, ChevronRight, User, CreditCard } from 'lucide-react';

const ProfileCompletionBanner = () => {
  const { profileStatus, isProfileComplete, profileCompletionPercentage, isFirmMember } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  // Check if banner was dismissed this session
  useEffect(() => {
    const dismissedKey = 'profile_banner_dismissed';
    const dismissedTime = sessionStorage.getItem(dismissedKey);
    if (dismissedTime) {
      setDismissed(true);
    }
  }, []);

  // Only show for firm members (staff/admin) who haven't completed their profile
  if (!isFirmMember || isProfileComplete || dismissed || !profileStatus) {
    return null;
  }

  const handleDismiss = () => {
    sessionStorage.setItem('profile_banner_dismissed', Date.now().toString());
    setDismissed(true);
  };

  const handleGoToProfile = () => {
    navigate('/profile');
  };

  // Determine what sections need attention
  const needsProfile = !profileStatus.profile_completed;
  const needsPayroll = !profileStatus.payroll_info_completed;

  return (
    <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 shadow-lg relative">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex-shrink-0">
            <AlertCircle className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <span className="font-semibold">Complete Your Profile</span>
              <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm">
                {profileCompletionPercentage}% complete
              </span>
            </div>
            <p className="text-sm text-white/90 mt-0.5">
              {needsProfile && needsPayroll ? (
                <>Your profile and payroll information are incomplete.</>
              ) : needsProfile ? (
                <>Complete your profile to appear on our website's About Us page.</>
              ) : (
                <>Complete your payroll information to enable direct deposit.</>
              )}
            </p>
            {profileStatus.missing_profile_fields?.length > 0 && (
              <div className="flex items-center space-x-3 mt-2 text-sm">
                {needsProfile && (
                  <span className="flex items-center space-x-1 bg-white/20 px-2 py-1 rounded">
                    <User className="w-3 h-3" />
                    <span>Profile: {profileStatus.missing_profile_fields.length} fields</span>
                  </span>
                )}
                {needsPayroll && (
                  <span className="flex items-center space-x-1 bg-white/20 px-2 py-1 rounded">
                    <CreditCard className="w-3 h-3" />
                    <span>Payroll: {profileStatus.missing_payroll_fields?.length || 0} fields</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleGoToProfile}
            className="flex items-center space-x-1 bg-white text-orange-600 px-4 py-2 rounded-lg font-medium hover:bg-orange-50 transition-colors"
          >
            <span>Complete Profile</span>
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            title="Dismiss for this session"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
        <div
          className="h-full bg-white transition-all duration-500"
          style={{ width: `${profileCompletionPercentage}%` }}
        />
      </div>
    </div>
  );
};

export default ProfileCompletionBanner;
